import * as React from 'react'
import styled from 'styled-components'

import { fromMd } from '@syconium/little-miss-figgy'

import {
  FetchKitQuery,
  StyledImageFragmentFragment,
} from '../../../../../../__generated__/graphql/catalog/graphql'
import { ContentfulColoredText } from '../../../../../../app/_components/pages/contentful/components/ContentfulColoredText'
import { ContentfulStyledImage } from '../../../../../../app/_components/pages/contentful/components/ContentfulStyledImage'
import { CompareAtPrice } from '../../../../../../app/_components/pricing/CompareAtPrice.client'
import { MarkedDownPrice } from '../../../../../../app/_components/pricing/MarkedDownPrice.client'
import { useLocalization } from '../../../../../../app/_providers/LocalizationProvider.client'
import { intlPrettyCurrency } from '../../../../../../lib/utils'
import { BadgesSection } from '../../../ProductPage/ProductHighlights/BadgesSection'
import { PriceDetails } from '../../../ProductPage/ProductHighlights/ProductHighlights.styles'

import {
  BadgesSectionContainer,
  Container,
  Description,
  Price,
  SiteWidePromoWrap,
  Title,
} from './styles'

export interface KitHighlightsProps {
  title: string | undefined
  description: string | undefined
  discountPrice?: number | null
  discountPriceExplicitFmt?: string | null
  price: number
  priceExplicitFmt?: string | null
  badges: ReadonlyArray<string> | undefined
  siteWidePromo: string | null
  swPromoEligible: boolean | undefined
  titleLockupImage: StyledImageFragmentFragment | undefined
  promotionalColoredTextSegments: NonNullable<
    FetchKitQuery['kit']
  >['promotionalColoredTextSegments']
}

export const KitHighlights: React.FC<KitHighlightsProps> = ({
  title,
  badges,
  description,
  price,
  priceExplicitFmt,
  discountPrice,
  siteWidePromo,
  swPromoEligible,
  titleLockupImage,
  promotionalColoredTextSegments,
}) => {
  const { locale, region } = useLocalization()

  const formattedPrice =
    priceExplicitFmt ??
    intlPrettyCurrency({ unit: 'cents', value: price, region: region, locale: locale })

  const formattedDiscountPrice =
    discountPrice &&
    intlPrettyCurrency({
      unit: 'cents',
      value: discountPrice,
      region: region,
      locale: locale,
    })
  return (
    <Container data-testid='kit-highlights'>
      {titleLockupImage ? (
        <ContentfulStyledImage
          widths={{
            unit: 'vw',
            sm: 50,
            md: 25,
          }}
          loading={'default'}
          {...titleLockupImage}
        />
      ) : (
        <Title asTag='h1'>{title}</Title>
      )}

      <PriceDetails>
        {discountPrice ? (
          <MarkedDownPrice>{formattedDiscountPrice}</MarkedDownPrice>
        ) : (
          <Price>{formattedPrice}</Price>
        )}
        {discountPrice ? <CompareAtPrice>{formattedPrice}</CompareAtPrice> : null}
      </PriceDetails>
      {badges && badges.length > 0 && (
        <BadgesSectionContainer>
          <BadgesSection badges={badges} />
        </BadgesSectionContainer>
      )}

      {promotionalColoredTextSegments.length > 0 ? (
        <StyledContentfulColoredText segments={promotionalColoredTextSegments} />
      ) : (
        siteWidePromo && swPromoEligible && <SiteWidePromoWrap>{siteWidePromo}</SiteWidePromoWrap>
      )}
      <Description asTag='p'>{description}</Description>
    </Container>
  )
}

const StyledContentfulColoredText = styled(ContentfulColoredText)`
  ${fromMd} {
    margin-top: ${o => o.theme.spacing(2)};
  }
`
export default KitHighlights
